import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Heart, ShoppingCart, Share2, Plus } from "lucide-react";
import "./ProductDetails.css";
import { useProductState, useProductActions } from "../contexts/ProductContext";
import Header from "./Header";
import Footer from "./Footer";
import ProductDrawer from "./ProductDrawer";
import ProductCard from "./ProductCard";
import { db } from "../services/firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { mergeProductsWithSpecs, normalizeString } from "./catalogUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { useBuyer } from "../contexts/BuyerContext";
import { downloadProductPoster } from "../utils/posterDownload";

const ProductDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const product = location.state?.product;
  const { buyer } = useBuyer();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [location.pathname, product?.id]);
  const share = buyer?.retailCustomerMode;
  const [specData, setSpecData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrawerProduct, setActiveDrawerProduct] = useState(null);
  const [selectedLotIndex, setSelectedLotIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [allMatchingProducts, setAllMatchingProducts] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [displayedLimit, setDisplayedLimit] = useState(5);
  const [sizes, setSizes] = useState([
    {
      size: "Regular",
      quantity: 1,
      availableQty: 0,
      isReadyStock: false,
      isExistingSize: false,
    },
  ]);

  const { likedProducts, selectedProducts } = useProductState();
  const { toggleLike, addToSelected } = useProductActions();
  console.log(product);
// ── fetch spec ──
  useEffect(() => {
    if (!product?.productName) {
      return;
    }

    const fetchSpec = async () => {
      try {
        const ns = (v) => normalizeString(v || "");

        const productSegment = normalizeString(
          product.segment || "",
        ).toLowerCase();
        const isGemsOrPearls =
          productSegment === "gems" || productSegment === "pearls";
        const isRudraksha =
          productSegment === "rudraksha" ||
          productSegment === "rudarkh" ||
          normalizeString(product.productName || "").toLowerCase() ===
            "rudraksha" ||
          normalizeString(product.productName || "")
            .toLowerCase()
            .includes("rudraksha");
        const isDecor = productSegment === "decor";
        const isDiamondOrPlatinum =
          productSegment === "diamond" || productSegment === "platinum";

        let snapshot;
        if (isRudraksha) {
          snapshot = await getDocs(collection(db, "productSpecs"));
        } else {
          const q = query(
            collection(db, "productSpecs"),
            where("segment", "==", product.segment),
            where("category", "==", product.category),
          );
          snapshot = await getDocs(q);
        }

        const allSpecs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const matchers = [
          (spec) =>
            spec.segment === product.segment &&
            spec.category === product.category &&
            ns(spec.productSource) === ns(product.productSource) &&
            ns(spec.productName) === ns(product.productName) &&
            ns(spec.finishType) === ns(product.finishType),
          (spec) =>
            spec.segment === product.segment &&
            spec.category === product.category &&
            ns(spec.productSource) === ns(product.productSource) &&
            ns(spec.productName) === ns(product.productName),
          (spec) =>
            spec.segment === product.segment &&
            spec.category === product.category &&
            ns(spec.productName) === ns(product.productName) &&
            ns(spec.finishType) === ns(product.finishType),
          (spec) =>
            spec.segment === product.segment &&
            spec.category === product.category &&
            ns(spec.productName) === ns(product.productName),
        ];

        let matchedSpec = null;
        for (const match of matchers) {
          matchedSpec = allSpecs.find(match);
          if (matchedSpec) break;
        }

        if (!isRudraksha && matchedSpec) {
          if (isGemsOrPearls) {
            const ok =
              matchedSpec.color &&
              product.color &&
              ns(matchedSpec.color) === ns(product.color) &&
              matchedSpec.shape &&
              product.shape &&
              ns(matchedSpec.shape) === ns(product.shape);
            if (!ok) matchedSpec = null;
          } else if (isDecor) {
            const ok =
              matchedSpec.color &&
              product.color &&
              ns(matchedSpec.color) === ns(product.color);
            if (!ok) matchedSpec = null;
          } else if (isDiamondOrPlatinum) {
            // Diamond/Platinum: accept any matched spec - specification is
            // embedded in the product document itself
          } else {
            const ok =
              matchedSpec.specification &&
              product.specification &&
              ns(matchedSpec.specification) === ns(product.specification);
            if (!ok) matchedSpec = null;
          }
        }

        setSpecData(matchedSpec || null);
      } catch {
        setSpecData(null);
      }
    };

    fetchSpec();
  }, [product]);

  // ── fetch similar products ──
  useEffect(() => {
    if (!product?.productName || !product?.segment || !product?.category) {
      return;
    }

    setDisplayedLimit(5);

    const fetchSimilarProducts = async () => {
      setLoadingSuggestions(true);
      try {
        // Query products from database
        const productsQuery = query(
          collection(db, "products"),
          limit(1000), // Limit to prevent massive queries
        );

        const productsSnapshot = await getDocs(productsQuery);

        if (productsSnapshot.empty) {
          setAllMatchingProducts([]);
          setLoadingSuggestions(false);
          return;
        }

        const allProducts = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch all specs
        const specsQuery = query(collection(db, "productSpecs"), limit(1000));
        const specsSnapshot = await getDocs(specsQuery);
        const allSpecs = specsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Calculate matching score for each product
        const calculateMatchScore = (prod) => {
          let score = 0;
          const criteria = [];

          // Check segment
          if (
            prod.segment?.toLowerCase().trim() ===
            product.segment?.toLowerCase().trim()
          ) {
            score++;
            criteria.push("segment");
          }

          // Check category
          if (
            prod.category?.toLowerCase().trim() ===
            product.category?.toLowerCase().trim()
          ) {
            score++;
            criteria.push("category");
          }

          // Check productName
          if (
            prod.productName?.toLowerCase().trim() ===
            product.productName?.toLowerCase().trim()
          ) {
            score++;
            criteria.push("productName");
          }

          // Check productSource
          if (
            prod.productSource?.toLowerCase().trim() ===
            (product.productSource || "").toLowerCase().trim()
          ) {
            score++;
            criteria.push("productSource");
          }

          // Check specification
          if (
            (prod.specification || "").toLowerCase().trim() ===
            (product.specification || "").toLowerCase().trim()
          ) {
            score++;
            criteria.push("specification");
          }

          // Check finishType/styleType
          if (
            (prod.finishType || prod.styleType || "").toLowerCase().trim() ===
            (product.styleType || product.finishType || "").toLowerCase().trim()
          ) {
            score++;
            criteria.push("finishType");
          }

          return { score, criteria };
        };

        // Filter and score products
        const scoredProducts = allProducts
          .filter(
            (prod) => prod.id !== product.id && prod.status === "approved",
          )
          .map((prod) => {
            const matchResult = calculateMatchScore(prod);
            return {
              ...prod,
              matchScore: matchResult.score,
              matchedCriteria: matchResult.criteria,
            };
          })
          .sort((a, b) => b.matchScore - a.matchScore);

        let filteredMatches = scoredProducts.filter(
          (prod) => prod.matchScore >= 4,
        );
        if (filteredMatches.length < 5) {
          filteredMatches = scoredProducts.filter(
            (prod) => prod.matchScore >= 2,
          );
        }
        if (filteredMatches.length < 5) {
          filteredMatches = scoredProducts.filter(
            (prod) => prod.matchScore >= 1,
          );
        }

        // Merge with specs
        const mergedSuggestions = mergeProductsWithSpecs(
          filteredMatches,
          allSpecs,
        );

        setAllMatchingProducts(mergedSuggestions);
      } catch (error) {
        console.error("Error fetching similar products:", error);
        setAllMatchingProducts([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    fetchSimilarProducts();
  }, [product]);

  const row1TrackRef = useRef(null);
  const row2TrackRef = useRef(null);
  const isNavigatingRef = useRef(false);

  const displayedSuggestions = useMemo(() => {
    return allMatchingProducts.slice(0, displayedLimit);
  }, [allMatchingProducts, displayedLimit]);

  const row1Products = useMemo(() => {
    return displayedSuggestions.filter((_, i) => i % 2 === 0);
  }, [displayedSuggestions]);

  const row2Products = useMemo(() => {
    return displayedSuggestions.filter((_, i) => i % 2 !== 0);
  }, [displayedSuggestions]);

  const min4Matches = useMemo(() => {
    return allMatchingProducts.filter(
      (p) => (p.matchScore || 0) >= 4 && p.id !== product?.id,
    );
  }, [allMatchingProducts, product?.id]);

  // Load next batch of 5 products on swipe / scroll
  const handleLoadMore = useCallback(() => {
    setDisplayedLimit((prev) => {
      if (prev < allMatchingProducts.length) {
        return Math.min(prev + 5, allMatchingProducts.length);
      }
      return prev;
    });
  }, [allMatchingProducts.length]);

  // Horizontal scroll trigger for loading 5 more on individual rows
  const handleRowScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 60) {
      handleLoadMore();
    }
  };

  // ── Scroll down to navigate to next product with min 4 matches ──
  useEffect(() => {
    if (min4Matches.length === 0) return;
    isNavigatingRef.current = false;

    const handleWindowScroll = () => {
      if (isNavigatingRef.current) return;

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      // When user scrolls near bottom of page (within 80px)
      if (scrollY + windowHeight >= docHeight - 80) {
        const nextProd = min4Matches[0];
        if (nextProd && nextProd.id !== product?.id) {
          isNavigatingRef.current = true;

          setTimeout(() => {
            navigate(`/product/${nextProd.id}`, {
              state: { product: nextProd },
              replace: false,
            });
            setSelectedImage(0);
            setSelectedLotIndex(0);
          }, 600);
        }
      }
    };

    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [min4Matches, product?.id, navigate]);

  // ── Open Drawer for a specific product (Main product or Suggested product) ──
  const handleOpenDrawerForProduct = async (prod) => {
    setActiveDrawerProduct(null);
    setDrawerOpen(true);
    setSizes([
      {
        size: "Regular",
        quantity: 1,
        availableQty: 0,
        isReadyStock: false,
        isExistingSize: false,
      },
    ]);

    try {
      // Fetch and merge spec for the product (like Catalog does)
      if (!prod?.productName) {
        setActiveDrawerProduct(prod);
        return;
      }

      const isRudraksha = (p) =>
        p.productName === "rudraksha" ||
        normalizeString(p.productName || "").toLowerCase() === "rudraksha" ||
        normalizeString(p.productName || "")
          .toLowerCase()
          .includes("rudraksha");

      const isRudrakshaVal = isRudraksha(prod);
      const productSegment = normalizeString(prod.segment || "").toLowerCase();
      const isGemsOrPearls =
        productSegment === "gems" || productSegment === "pearls";
      const isDecor = productSegment === "decor";
      const isDiamondOrPlatinum =
        productSegment === "diamond" || productSegment === "platinum";

      let snapshot;
      if (isRudrakshaVal) {
        snapshot = await getDocs(collection(db, "productSpecs"));
      } else {
        const q = query(
          collection(db, "productSpecs"),
          where("segment", "==", prod.segment),
          where("category", "==", prod.category),
        );
        snapshot = await getDocs(q);
      }

      const allSpecs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const ns = (v) => normalizeString(v || "");

      const matchers = [
        (spec) =>
          spec.segment === prod.segment &&
          spec.category === prod.category &&
          ns(spec.productSource) === ns(prod.productSource) &&
          ns(spec.productName) === ns(prod.productName) &&
          ns(spec.finishType) === ns(prod.finishType),
        (spec) =>
          spec.segment === prod.segment &&
          spec.category === prod.category &&
          ns(spec.productSource) === ns(prod.productSource) &&
          ns(spec.productName) === ns(prod.productName),
        (spec) =>
          spec.segment === prod.segment &&
          spec.category === prod.category &&
          ns(spec.productName) === ns(prod.productName) &&
          ns(spec.finishType) === ns(prod.finishType),
        (spec) =>
          spec.segment === prod.segment &&
          spec.category === prod.category &&
          ns(spec.productName) === ns(prod.productName),
      ];

      let matchedSpec = null;
      for (const match of matchers) {
        matchedSpec = allSpecs.find(match);
        if (matchedSpec) break;
      }

      if (!isRudrakshaVal && matchedSpec) {
        if (isGemsOrPearls) {
          const ok =
            matchedSpec.color &&
            prod.color &&
            ns(matchedSpec.color) === ns(prod.color) &&
            matchedSpec.shape &&
            prod.shape &&
            ns(matchedSpec.shape) === ns(prod.shape);
          if (!ok) matchedSpec = null;
        } else if (isDecor) {
          const ok =
            matchedSpec.color &&
            prod.color &&
            ns(matchedSpec.color) === ns(prod.color);
          if (!ok) matchedSpec = null;
        } else if (isDiamondOrPlatinum) {
          // Diamond/Platinum: accept any matched spec - specification is
          // embedded in the product document itself
        } else {
          const ok =
            matchedSpec.specification &&
            prod.specification &&
            ns(matchedSpec.specification) === ns(prod.specification);
          if (!ok) matchedSpec = null;
        }
      }

      const merged = matchedSpec
        ? mergeProductsWithSpecs([prod], [matchedSpec])[0]
        : { ...prod, hasSpecification: false, specId: null };

      setActiveDrawerProduct(merged);
    } catch (error) {
      console.error("Error fetching spec for suggestion product:", error);
      setActiveDrawerProduct({
        ...prod,
        hasSpecification: false,
        specId: null,
      });
    }
  };

  // ── Handle product card click (navigate to details) ──
  const handleSuggestionClick = (suggestionProduct) => {
    if (suggestionProduct.id === product?.id) return;

    navigate(`/product/${suggestionProduct.id}`, {
      state: { product: suggestionProduct },
      replace: false,
    });

    setSelectedImage(0);
    setSelectedLotIndex(0);
  };

  const mergedProduct = specData
    ? mergeProductsWithSpecs([product], [specData])[0]
    : product;

  const isLotBased = mergedProduct?.lotDetails?.length > 0;
  const selectedLot = isLotBased
    ? mergedProduct.lotDetails[selectedLotIndex]
    : null;
  const isLiked = likedProducts.includes(mergedProduct?.id);
  const isSelected = selectedProducts.some((p) => p.id === mergedProduct?.id);

  // ── Render suggestions section ──
  const renderSuggestionsSection = () => {
    if (loadingSuggestions) {
      return (
        <div className="suggestions-section">
          <div className="suggestions-header">
            <h3>Similar Products</h3>
          </div>
          <div className="suggestions-loading-state">
            <span className="spinner-small" />
            <p>Finding similar products...</p>
          </div>
        </div>
      );
    }

    if (allMatchingProducts.length === 0) {
      return null;
    }

    return (
      <div className="suggestions-section">
        <div className="suggestions-header">
          <h3>Similar Products</h3>
        </div>

        <div className="suggestions-rows-container">
          {/* Line 1 */}
          <div className="suggestions-row-wrapper">
            <div
              className="suggestions-single-row-track"
              ref={row1TrackRef}
              onScroll={handleRowScroll}
            >
              {row1Products.map((suggestion, index) => {
                const suggestionLiked = likedProducts.includes(suggestion.id);
                const suggestionSelected = selectedProducts.some(
                  (p) => p.id === suggestion.id,
                );

                return (
                  <ProductCard
                    key={`r1-${suggestion.id}-${index}`}
                    product={suggestion}
                    isLiked={suggestionLiked}
                    isSelected={suggestionSelected}
                    onProductClick={() => handleSuggestionClick(suggestion)}
                    onLikeClick={(e) => {
                      e.stopPropagation();
                      toggleLike(suggestion.id);
                    }}
                    onSelectedClick={(e, prod) => {
                      e.stopPropagation();
                      handleOpenDrawerForProduct(prod || suggestion);
                    }}
                    showQuickView={false}
                    showPrice={true}
                    showStock={true}
                  />
                );
              })}

              {displayedLimit < allMatchingProducts.length && (
                <div
                  className="suggestions-row-load-card"
                  onClick={handleLoadMore}
                  role="button"
                  tabIndex={0}
                  aria-label="Load 5 more suggestions"
                >
                  <Plus size={20} />
                  <span>+5 More</span>
                </div>
              )}
            </div>
          </div>

          {/* Line 2 */}
          {row2Products.length > 0 && (
            <div className="suggestions-row-wrapper">
              <div
                className="suggestions-single-row-track"
                ref={row2TrackRef}
                onScroll={handleRowScroll}
              >
                {row2Products.map((suggestion, index) => {
                  const suggestionLiked = likedProducts.includes(suggestion.id);
                  const suggestionSelected = selectedProducts.some(
                    (p) => p.id === suggestion.id,
                  );

                  return (
                    <ProductCard
                      key={`r2-${suggestion.id}-${index}`}
                      product={suggestion}
                      isLiked={suggestionLiked}
                      isSelected={suggestionSelected}
                      onProductClick={() => handleSuggestionClick(suggestion)}
                      onLikeClick={(e) => {
                        e.stopPropagation();
                        toggleLike(suggestion.id);
                      }}
                      onSelectedClick={(e, prod) => {
                        e.stopPropagation();
                        handleOpenDrawerForProduct(prod || suggestion);
                      }}
                      showQuickView={false}
                      showPrice={true}
                      showStock={true}
                    />
                  );
                })}

                {displayedLimit < allMatchingProducts.length && (
                  <div
                    className="suggestions-row-load-card"
                    onClick={handleLoadMore}
                    role="button"
                    tabIndex={0}
                    aria-label="Load 5 more suggestions"
                  >
                    <Plus size={20} />
                    <span>+5 More</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── calculations ──
  const num = (v) => parseFloat(v) || 0;

  const baseWastage =
    num(mergedProduct.wastage) +
    num(mergedProduct.wastageOperator) +
    num(mergedProduct.wastageJmi) +
    num(mergedProduct.specificationMC);

  const hasSpecification =
    mergedProduct.specification &&
    normalizeString(mergedProduct.specification) !== "PLANE";
  const specAdditions = hasSpecification
    ? (num(mergedProduct.specificationMC) *
        num(mergedProduct.specificationMakingJmi)) /
        100 +
      (num(mergedProduct.specificationMC) *
        num(mergedProduct.specificationMakingOperator)) /
        100
    : 0;
  const wastage = baseWastage + specAdditions;

  const setMcBase = num(
    mergedProduct.setMc || mergedProduct.lotDetails[0].setMc,
  );
  const setMc =
    setMcBase +
    setMcBase *
      ((num(mergedProduct.setMakingJmi) +
        num(mergedProduct.setMakingOperator)) /
        100);

  const gramMcPerGram =
    num(mergedProduct.netGramMc || mergedProduct.lotDetails[0].netGramMc) *
    (1 +
      (num(mergedProduct.gramMakingJmi) +
        num(mergedProduct.gramMakingOperator)) /
        100);

  const specMcPerGram =
    num(
      mergedProduct.specificationGramRate ||
        mergedProduct.lotDetails[0].specificationGramRate,
    ) *
    (1 +
      (num(mergedProduct.specificationGramRateJmi) +
        num(mergedProduct.specificationGramRateOperator)) /
        100);

  const TotalAmount = () => {
    const total =
      setMc +
      gramMcPerGram *
        parseFloat(
          selectedLot?.avgNetWt || mergedProduct.lotDetails[0].avgNetWt,
        ) +
      specMcPerGram *
        parseFloat(
          selectedLot?.avgSpecWt || mergedProduct.lotDetails[0].avgSpecWt,
        );
    return total.toFixed(2);
  };

  const calculateFineWeight = () => {
    const netWeight = num(
      selectedLot?.avgNetWt || mergedProduct.lotDetails[0].avgNetWt,
    );
    const fineWeight =
      (netWeight * (num(mergedProduct.buyerDeskCalculation) + num(wastage))) /
      100;
    return fineWeight.toFixed(3);
  };

  const displayPurity = mergedProduct.category?.replace(
    /(\d+)(HUID|ORNA)/i,
    "$1 $2",
  );

  const calculateApproxNetWt = (variant) => {
    if (!mergedProduct.lotDetails || !variant) return null;
    const lotDetail = mergedProduct.lotDetails.find(
      (l) => l.size === variant.size,
    );
    return lotDetail?.avgNetWt
      ? `~${(parseFloat(lotDetail.avgNetWt) * variant.quantity).toFixed(3)} g`
      : null;
  };

  const calculateSetForNonLot = (variant) => {
    if (mergedProduct.lot || !variant || !mergedProduct.netWt) return null;
    return ` Set ~${(
      parseFloat(mergedProduct.netWt) * variant.quantity
    ).toFixed(3)}`;
  };

  // ── handlers ──
  const handleLikeClick = () => toggleLike(mergedProduct.id);
  const handleAddToCart = () => {
    setActiveDrawerProduct(mergedProduct);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActiveDrawerProduct(null);
    setSizes([
      {
        size: "Regular",
        quantity: 1,
        availableQty: 0,
        isReadyStock: false,
        isExistingSize: false,
      },
    ]);
  };

  const handleSizeChange = (newSizes) => setSizes(newSizes);
  const handleAddToSelection = (productWithVariants, serviceType) => {
    if (!productWithVariants) return;
    addToSelected({
      ...productWithVariants,
      serviceType: serviceType || "order",
    });
    setDrawerOpen(false);
    setActiveDrawerProduct(null);
  };

  // ── share / download ──
  const handleShare = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const makingAmount = parseFloat(TotalAmount()) || 0;

      console.log("[ProductDetails] Starting download for:", {
        product: mergedProduct?.productName,
        segment: mergedProduct?.segment,
        makingAmount,
      });

      const result = await downloadProductPoster(
        mergedProduct,
        buyer,
        makingAmount,
        {
          fileName: `${mergedProduct.productName}-${mergedProduct.segment}`,
        },
      );

      if (result && result.success) {
        console.log("[ProductDetails] Download completed successfully");
      }
    } catch (err) {
      console.error("[ProductDetails] Download failed:", err);
      const errorMsg = err?.message || "Unknown error occurred";
      alert(`Download failed: ${errorMsg}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // ── render helpers ──
  const renderImageGallery = () => (
    <div className="product-gallery">
      <div className="main-image">
        {mergedProduct.images && mergedProduct.images.length > 0 ? (
          <>
            <img
              src={mergedProduct.images[selectedImage]?.url}
              alt={mergedProduct.productName}
            />
            <div className="image-overlay-actions">
              <button
                className={`like-btn image-like-btn ${isLiked ? "liked" : ""}`}
                onClick={handleLikeClick}
                title={isLiked ? "Remove from wishlist" : "Add to wishlist"}
              >
                <Heart size={22} fill={isLiked ? "currentColor" : "none"} />
              </button>
            </div>
          </>
        ) : (
          <div className="image-placeholder">
            No Image Available
            <div className="image-overlay-actions">
              <button
                className={`like-btn image-like-btn ${isLiked ? "liked" : ""}`}
                onClick={handleLikeClick}
              >
                <Heart size={22} fill={isLiked ? "currentColor" : "none"} />
              </button>
            </div>
          </div>
        )}
      </div>

      {mergedProduct.images?.length > 1 && (
        <div className="thumbnail-container">
          {mergedProduct.images.map((image, index) => (
            <div
              key={index}
              className={`thumbnail ${selectedImage === index ? "active" : ""}`}
              onClick={() => setSelectedImage(index)}
            >
              <img src={image.url} alt={`Thumbnail ${index + 1}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderVariantInfo = () => {
    if (mergedProduct.selectedVariants?.length > 0) {
      return mergedProduct.selectedVariants.map((variant, index) => (
        <span key={variant.size} className="variant-info">
          {variant.size}
          {variant.quantity > 1 && ` x ${variant.quantity}`}
          {mergedProduct.lot && calculateApproxNetWt(variant) && (
            <span className="approx-weight">
              ({calculateApproxNetWt(variant)})
            </span>
          )}
          {!mergedProduct.lot && calculateSetForNonLot(variant) && (
            <span className="set-info">{calculateSetForNonLot(variant)}</span>
          )}
          {index < mergedProduct.selectedVariants.length - 1 && ", "}
        </span>
      ));
    }

    if (isLotBased) {
      return (
        <div>
          <div className="lot-chips">
            {mergedProduct.lotDetails.map((lot, index) => (
              <button
                key={index}
                className={`chip ${
                  selectedLotIndex === index ? "chip-selected" : ""
                }`}
                onClick={() => setSelectedLotIndex(index)}
              >
                {lot.size || `Lot ${index + 1}`}
              </button>
            ))}
          </div>
          <div className="spec-lot-item">
            <span className="spec-label">
              Net wt ~ {parseFloat(selectedLot?.avgNetWt || 0).toFixed(3)} g
            </span>
            <span className="spec-label">Avl set: {selectedLot.set}</span>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="lot-chips">
          <button className="chip chip-selected">
            {mergedProduct.lotDetails[0].size}
          </button>
        </div>
        <div className="spec-lot-item">
          <span className="spec-label">
            Net wt ~{" "}
            {parseFloat(mergedProduct.lotDetails[0].avgNetWt || 0).toFixed(3)} g
          </span>
          <span className="spec-label">
            Avl set: {mergedProduct.lotDetails[0].set}
          </span>
        </div>
      </div>
    );
  };

  const renderSpecifications = () => {
    if (isLotBased && !selectedLot) {
      return <div className="error">Lot details not available</div>;
    }

    return (
      <div className="specifications-card">
        <h2>Specifications</h2>
        <div className="spec-grid">
          <div>
            <SpecRow
              label="Status"
              value={
                mergedProduct.serviceType === "ready"
                  ? "Ready Serve"
                  : "Order Serve"
              }
            />
            <SpecRow label="Segment" value={mergedProduct.segment} />
            <SpecRow label="Category" value={mergedProduct.category} />
            <SpecRow
              label="Purity"
              value={`${parseFloat(
                mergedProduct.buyerDeskCalculation || 0,
              ).toFixed(2)}%`}
            />
            <SpecRow
              label="Product"
              value={`${mergedProduct.productSource} ${mergedProduct.productName}`}
            />
            <SpecRow
              label="Specification"
              value={mergedProduct.specification}
            />
            <SpecRow
              label="Finish Type"
              value={
                mergedProduct.finishType || mergedProduct.styleType || "Regular"
              }
            />
            <SpecRow label="Settlement" value={mergedProduct.paymentMethod} />
          </div>
          <div>
            <SpecRow label="Set" value={1} />
            <SpecRow
              label="Gross Wt."
              value={`~${parseFloat(
                isLotBased
                  ? selectedLot?.avgGrossWt
                  : mergedProduct.lotDetails[0].avgGrossWt || 0,
              ).toFixed(3)} g`}
            />
            {normalizeString(mergedProduct.specification) !== "PLANE" && (
              <SpecRow
                label={`${mergedProduct.specification || "Spec"} Wt.`}
                value={`~${parseFloat(
                  isLotBased
                    ? selectedLot?.avgSpecWt
                    : mergedProduct.lotDetails[0].avgSpecWt || 0,
                ).toFixed(3)} g`}
              />
            )}
            <SpecRow
              label="Net Wt."
              value={`~${parseFloat(
                isLotBased
                  ? selectedLot?.avgNetWt
                  : mergedProduct.lotDetails[0].avgNetWt || 0,
              ).toFixed(3)} g`}
            />
            <SpecRow
              label="Wastage"
              value={`${parseFloat(wastage || 0).toFixed(2)}%`}
            />
            <SpecRow label="Set Mc." value={`~₹${setMc.toFixed(2)}`} />
            <SpecRow
              label="Net Gram Mc."
              value={`~₹${gramMcPerGram.toFixed(2)}/g`}
            />
            {normalizeString(mergedProduct.specification) !== "PLANE" && (
              <SpecRow
                label={`${mergedProduct.specification || "Spec"} Wt. Per gm`}
                value={`~₹${specMcPerGram.toFixed(2)}/g`}
              />
            )}
          </div>
        </div>

        <div className="rtgs-section">
          <div>
            <strong>Fine</strong>
            <div className="price">~ {calculateFineWeight()} g</div>
          </div>
          <div>
            <strong>Amount</strong>
            <div className="price">~₹{TotalAmount()}</div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className={`add-to-cart-btn ${isSelected ? "selected" : ""}`}
            onClick={handleAddToCart}
          >
            {isSelected ? (
              <>
                <ShoppingCart size={16} /> SELECTED
              </>
            ) : (
              "SELECT"
            )}
          </button>
        </div>
      </div>
    );
  };

  // ── JSX ──
  return (
    <>
      <Header
        title={`${mergedProduct.segment} ${displayPurity} ${mergedProduct.productName}`}
        showBack={true}
      />

      <div className="product-details-container">
        <div className="product-main-section">
          <div className="product-media-section">
            <div className="product-content">{renderImageGallery()}</div>
            <div className="additional-info">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Single share button — downloads poster directly, no modal */}
                {share && (
                  <button
                    className="share-action-btn"
                    onClick={handleShare}
                    disabled={isDownloading}
                    title="Download product poster"
                  >
                    {isDownloading ? (
                      <span className="spinner-small" />
                    ) : (
                      <Share2 size={20} />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="product-info">
            {" "}
            <div>
              {mergedProduct.serviceType === "ready"
                ? "Ready Serve"
                : "Order Serve"}{" "}
              {mergedProduct.productSource} {mergedProduct.productName}
            </div>
            <span className="spec-label">{renderVariantInfo()}</span>
            {renderSpecifications()}
          </div>
        </div>

        {/* Add suggestions section */}
        {renderSuggestionsSection()}
      </div>

      <ProductDrawer
        isOpen={drawerOpen}
        product={activeDrawerProduct || mergedProduct}
        sizes={sizes}
        serviceType={
          activeDrawerProduct?.serviceType ||
          mergedProduct?.serviceType ||
          "order"
        }
        onClose={closeDrawer}
        onSizeChange={handleSizeChange}
        onAddToSelection={handleAddToSelection}
      />

      <Footer />
    </>
  );
};

const SpecRow = ({ label, value }) => (
  <div className="spec-row">
    <span className="spec-label">{label}</span>
    <span className="spec-value">{value}</span>
  </div>
);

export default ProductDetails;
